import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { Layout, LayoutContent, LayoutFooter, HStack, VStack } from "@astryxdesign/core/Layout";
import { FormLayout } from "@astryxdesign/core/FormLayout";
import { TextInput } from "@astryxdesign/core/TextInput";
import { CheckboxList, CheckboxListItem } from "@astryxdesign/core/CheckboxList";
import { Divider } from "@astryxdesign/core/Divider";
import { Button } from "@astryxdesign/core/Button";
import { Text } from "@astryxdesign/core/Text";
import { Banner } from "@astryxdesign/core/Banner";
import {
  type CameraScope,
  type CreateRoleInput,
  type PermissionCatalogEntry,
  type Role,
  type UpdateRoleInput,
  api,
  isUnrestrictedScope,
  unrestrictedScope,
} from "../../lib/api";
import { CameraAccessEditor } from "../access/CameraAccessEditor";

interface RoleFormValues {
  name: string;
  permissions: string[];
  isRestricted: boolean;
  cameraScope: CameraScope;
}

interface FieldErrors {
  name?: string;
  permissions?: string;
  cameraScope?: string;
}

const emptyValues: RoleFormValues = { name: "", permissions: [], isRestricted: false, cameraScope: unrestrictedScope };

interface RoleFormDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present for edit, absent for create. Never a system role — RolesPage
   *  disables the edit action for those before this dialog ever opens. */
  editing: Role | null;
  catalog: PermissionCatalogEntry[];
  isSaving: boolean;
  onCreate: (input: CreateRoleInput) => Promise<unknown>;
  onUpdate: (id: string, input: UpdateRoleInput) => Promise<unknown>;
}

// One dialog handles both create and edit, same reasoning as
// UserFormDialog and CameraFormDialog before it.
export function RoleFormDialog({ isOpen, onOpenChange, editing, catalog, isSaving, onCreate, onUpdate }: RoleFormDialogProps) {
  const [values, setValues] = useState<RoleFormValues>(emptyValues);
  // Field-level errors mark the offending input directly (status="error")
  // instead of a single string at the bottom of the form — requestError is
  // reserved for failures the fields themselves can't predict.
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [requestError, setRequestError] = useState<string | null>(null);

  // Projects and cameras are fetched here (not passed down) for the same
  // reason UserFormDialog fetches roles itself: this dialog is the only
  // place that needs them, to build the access editor's tree.
  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: api.projects.list, enabled: isOpen });
  const { data: cameras } = useQuery({ queryKey: ["cameras"], queryFn: api.cameras.list, enabled: isOpen });

  useEffect(() => {
    if (!isOpen) return;
    setFieldErrors({});
    setRequestError(null);
    if (editing) {
      setValues({
        name: editing.name,
        permissions: editing.permissions,
        isRestricted: !isUnrestrictedScope(editing.camera_scope),
        cameraScope: editing.camera_scope,
      });
    } else {
      setValues(emptyValues);
    }
  }, [isOpen, editing]);

  const handleSubmit = async () => {
    setRequestError(null);

    const errors: FieldErrors = {};
    if (!values.name.trim()) errors.name = "Name is required.";
    if (values.permissions.length === 0) errors.permissions = "Select at least one permission.";
    if (values.isRestricted && isUnrestrictedScope(values.cameraScope)) {
      errors.cameraScope = "Select at least one project or camera, or turn off camera restriction.";
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const cameraScope = values.isRestricted ? values.cameraScope : unrestrictedScope;

    try {
      if (editing) {
        await onUpdate(editing.id, {
          name: values.name.trim(),
          permissions: values.permissions,
          camera_scope: cameraScope,
          version: editing.version,
        });
      } else {
        await onCreate({ name: values.name.trim(), permissions: values.permissions, camera_scope: cameraScope });
      }
      onOpenChange(false);
    } catch (err) {
      setRequestError(err instanceof Error ? err.message : "Something went wrong.");
    }
  };

  // No <form> element wraps this dialog's fields — see CameraFormDialog's
  // own comment on why TextInput's onEnter is used instead.
  const submitOnEnter = () => {
    if (!isSaving) void handleSubmit();
  };

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} purpose="form" width={560}>
      <Layout
        header={
          <DialogHeader
            title={editing ? `Edit ${editing.name}` : "Add role"}
            subtitle="Choose exactly what accounts with this role can do."
            onOpenChange={onOpenChange}
          />
        }
        content={
          <LayoutContent>
            <FormLayout>
              <TextInput
                label="Name"
                value={values.name}
                onChange={(v) => setValues((prev) => ({ ...prev, name: v }))}
                status={fieldErrors.name ? { type: "error", message: fieldErrors.name } : undefined}
                isRequired
                hasAutoFocus
                onEnter={submitOnEnter}
              />
              <CheckboxList
                label="Permissions"
                value={values.permissions}
                onChange={(permissions) => setValues((prev) => ({ ...prev, permissions }))}
                status={fieldErrors.permissions ? { type: "error", message: fieldErrors.permissions } : undefined}
                hasDividers
              >
                {catalog.map((entry) => (
                  <CheckboxListItem key={entry.key} value={entry.key} label={entry.label} description={entry.description} />
                ))}
              </CheckboxList>

              <Divider />

              <VStack gap={0.5}>
                <Text weight="medium">Camera access</Text>
                <Text type="supporting" color="secondary">
                  The default camera/project scope for accounts with this role. A user's own override, if set, replaces
                  this rather than combining with it.
                </Text>
              </VStack>
              <CameraAccessEditor
                isRestricted={values.isRestricted}
                onRestrictedChange={(isRestricted) => setValues((prev) => ({ ...prev, isRestricted }))}
                scope={values.cameraScope}
                onScopeChange={(cameraScope) => setValues((prev) => ({ ...prev, cameraScope }))}
                projects={projects ?? []}
                cameras={cameras ?? []}
                status={fieldErrors.cameraScope ? { type: "error", message: fieldErrors.cameraScope } : undefined}
              />

              {requestError && <Banner status="error" title="Couldn't save role" description={requestError} />}
            </FormLayout>
          </LayoutContent>
        }
        footer={
          <LayoutFooter>
            <HStack gap={2} hAlign="end">
              <Button label="Cancel" variant="ghost" onClick={() => onOpenChange(false)} isDisabled={isSaving} />
              <Button
                label={editing ? "Save changes" : "Add role"}
                variant="primary"
                clickAction={handleSubmit}
                isLoading={isSaving}
              />
            </HStack>
          </LayoutFooter>
        }
      />
    </Dialog>
  );
}
